import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { Lightbox } from "react-modal-image";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import { uploadFile } from "../../requests/utilities/requests";

// HEIC/HEIF are accepted for upload but the backend re-encodes them to JPEG
// (browsers other than Safari can't render them); until that runs they show
// as a generic file rather than a broken image.
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "svg"];
const isImage = (ext) => IMAGE_EXTENSIONS.includes((ext || "").toLowerCase());

/**
 * Attachments for a single daily note (event). Mirrors BoardCardAttachments:
 * uploads via the shared presigned-URL helper, then links the file to the note.
 *
 * The note must already be saved (it needs an id to own the FK), so this is
 * rendered on existing note cards. If the attach/remove callbacks or companyId
 * aren't wired through (some render paths may not pass them), it degrades to a
 * read-only download list.
 */
const EventNoteAttachments = ({ event, companyId, attachEventFile, removeEventFile, deleteUploadedFile }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const files = event?.attachments || [];
  const canEdit = Boolean(companyId && attachEventFile && removeEventFile);

  // Images open in an in-app lightbox; everything else opens in a new tab.
  // Mirrors the maintenance attachment pattern.
  const openFile = (f) => {
    if (!f.presigned_url) return;
    if (isImage(f.extension)) {
      setLightboxImage(f.presigned_url);
    } else {
      window.open(f.presigned_url, "_blank", "noopener,noreferrer");
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Reset the input so selecting the same file again still fires onChange.
    e.target.value = "";
    if (!file) return;

    setUploading(true);
    let fileUuid = null;
    let attached = false;
    try {
      const payload = await uploadFile(companyId, file, "event");
      fileUuid = (payload && payload.uuid) || null;
      if (!fileUuid) throw new Error("Upload did not return a file");
      await attachEventFile(event.id, fileUuid);
      attached = true;
    } catch (error) {
      toast.error("Failed to upload attachment");
      // Orphan from either a failed attach (fileUuid set) or a failed S3
      // PUT/metadata fetch after the row was created (uuid on the error).
      const orphanUuid = fileUuid || error?.fileUploadUuid || null;
      if (orphanUuid && !attached && deleteUploadedFile) {
        deleteUploadedFile(orphanUuid);
      }
    } finally {
      setUploading(false);
    }
  };

  const removeFile = async (fileUuid) => {
    try {
      await removeEventFile(event.id, fileUuid);
    } catch (error) {
      toast.error("Failed to remove attachment");
    }
  };

  if (!canEdit && files.length === 0) return null;

  return (
    <div className="mt-4 border-t border-slate-200 pt-4">
      {lightboxImage && (
        <div className="absolute w-screen h-screen z-50">
          <Lightbox
            small={lightboxImage}
            medium={lightboxImage}
            hideDownload={true}
            hideZoom={true}
            onClose={() => setLightboxImage(null)}
          />
        </div>
      )}
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-xs font-semibold text-slate-500">ATTACHMENTS</p>
        {canEdit && (
          <button
            className="flex items-center gap-1 text-xs font-medium text-violet-700 hover:text-violet-800 disabled:text-slate-400"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={uploading}
          >
            <AddIcon style={{ fontSize: 15 }} />
            {uploading ? "Uploading…" : "Add"}
          </button>
        )}
      </div>
      {canEdit && (
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileSelect}
        />
      )}

      {files.length === 0 ? (
        <p className="text-xs text-slate-400">No attachments</p>
      ) : (
        <div className="space-y-1.5">
          {files.map((f) => (
            <div
              key={f.uuid}
              className="group flex items-center gap-2 border border-slate-200 rounded-lg px-2.5 py-2"
            >
              {isImage(f.extension) ? (
                <img
                  src={f.presigned_url}
                  alt={f.displayName || f.name}
                  className="w-9 h-9 rounded object-cover shrink-0 cursor-pointer"
                  onClick={() => openFile(f)}
                />
              ) : (
                <InsertDriveFileIcon
                  style={{ fontSize: 18 }}
                  className="text-slate-400 shrink-0"
                />
              )}
              <button
                onClick={() => openFile(f)}
                className="flex-1 min-w-0 text-left text-sm text-slate-700 hover:text-violet-700 truncate"
              >
                {f.displayName || f.name}
              </button>
              {f.fileSize && (
                <span className="text-xs text-slate-400 shrink-0">{f.fileSize}</span>
              )}
              {canEdit && (
                <button
                  className="flex items-center text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 shrink-0"
                  onClick={() => removeFile(f.uuid)}
                  title="Remove attachment"
                >
                  <CloseIcon style={{ fontSize: 16 }} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventNoteAttachments;
